/**
 * ¿La llamada de una alarma sigue "viva" (el llamante/paciente no ha colgado)?
 *
 * Fuente de verdad: `operator.workingAlerts` (guardado en el store como
 * `alarmQueue.myItems`), que el poll refresca en cada ciclo con el estado real
 * de la conferencia desde SAC.
 *
 * Semántica (según tmc-sac):
 *  - Si el operador está ATENDIENDO la llamada y el llamante cuelga, SAC cierra
 *    la alerta y borra su fila de WORKING_ALERTS → la alarma DESAPARECE de
 *    `workingAlerts`. Por eso "no está en myItems" = colgada.
 *  - Si la conferencia se pausa (p.ej. el paciente de una videollamada sale),
 *    `conference.running` pasa a `false` (la fila se mantiene) → también muerta.
 *
 * Deliberadamente NO se exige que haya un miembro remoto en la conferencia:
 * durante una SALIENTE sonando aún no ha entrado el paciente y la llamada sí
 * está viva; exigir miembro remoto bloquearía el botón de colgar en ese caso.
 *
 * Latencia:
 *  - Si cuelga el OPERADOR (CallEnd / close): la propia respuesta del close trae
 *    `operator.workingAlerts`, que `postCloseAlarmAsync` aplica al store al
 *    instante → inmediato, sin esperar al poll.
 *  - Si cuelga el OTRO lado: SAC actualiza `lastUpdate`, el poll ligero lo
 *    detecta y pide la cola fresca → en el siguiente tick de lastUpdate.
 */
export const isCallAlive = (uuid: string | undefined | null, myItems: any[] | undefined | null): boolean => {
  if (!uuid) return false;
  const workingAlert = (myItems ?? []).find((alert: any) => alert?.uuid === uuid);
  if (!workingAlert) return false; // cerrada / fuera de workingAlerts
  if (workingAlert.conference?.running === false) return false; // conferencia pausada
  return true;
};

/** Tipos de alerta que llevan sala de llamada (entrantes, callbacks y salientes). */
export const CALL_ALERT_TYPES = [
  'A',
  'CALLBACK',
  'Callback',
  'CallbackIp',
  'CallbackVideo',
  'OUTGOING',
  'OUTGOING_IP',
  'OUTGOING_VIDEO',
];

export const isCallAlert = (alert: any): boolean =>
  !!alert && (alert.callAlarm === true || CALL_ALERT_TYPES.includes(alert.type));

const alertTmst = (alert: any): number => Number(alert?.alertTimestamp ?? 0) || 0;

/**
 * Llamada EN CURSO del operador, con independencia del evento que tenga seleccionado: una
 * working alert de llamada, con conferencia (hay sala) y viva. La más reciente si hubiera
 * varias. Es lo que mantiene la ventana de Jitsi montada mientras el operador cambia el foco
 * entre los eventos del expediente: desmontarla cortaba su pata de la llamada y cada
 * reselección entraba en la sala como un participante más (PRE 16/10).
 */
export const findActiveCall = (myItems: any[] | undefined | null): any | null =>
  (myItems ?? [])
    .filter((alert: any) => isCallAlert(alert) && !!alert?.conference && isCallAlive(alert?.uuid, myItems))
    .sort((a: any, b: any) => alertTmst(b) - alertTmst(a))[0] ?? null;
